import { createContext, useContext, useEffect } from 'react'
import {
  QueryClientProvider,
  QueryClient,
  useQuery,
  UseQueryResult,
} from 'react-query'
import { useRouter } from 'next/router'
import type { UserBasicApiResponseType } from './types/user-basic'
import type { UserProfileApiResponseType } from './types/user-profile'
import { getUpdatedHeaders } from './utils'
import { getFetchRequest } from '@/utils/network-adapter/fetch-request'
import { USER_API_URL, USER_PROFILE_URL } from '@/root/constants'
import { WithChildren } from '@/root/types/utilities'
import { addParameterToURL } from '@/root/helpers'
import useGetAuthToken from '@/hooks/use-get-auth-token'
import useClientSessionStorage from '@/hooks/use-get-session-id'

export type TAuthenticationState = 'LOGGED_OUT' | 'LOGGED_IN'

type UserApiDataContextType =
  | {
      userBasicRQData: UseQueryResult<UserBasicApiResponseType, unknown>
      userProfileRQData: UseQueryResult<UserProfileApiResponseType, unknown>
      isAuthenticated: boolean
      isNotAuthenticated: boolean
      isLoadingAuthenticated: boolean
      authenticationState: TAuthenticationState
    }
  | undefined

export const UserRQContext = createContext<UserApiDataContextType>(undefined)

export function useUserRQInfo() {
  const context = useContext<UserApiDataContextType>(UserRQContext)

  if (context === undefined) {
    throw new Error(
      'useUserRQInfo must be used within a UserRQContext Context Provider',
    )
  }
  return context
}

function UserFetcher({ children }: WithChildren) {
  const authenticationToken = useGetAuthToken()
  const headers = { Authorization: `Bearer ${authenticationToken}` }
  const router = useRouter()
  const pathname = router.pathname
  const asPath = router.asPath
  const { setUserId, userData } = useClientSessionStorage()

  const userBasicRQData = useQuery(
    ['user-basic', router.pathname],
    (): Promise<UserBasicApiResponseType> =>
      getFetchRequest({
        url: USER_API_URL,
        config: {
          headers: getUpdatedHeaders(
            headers,
            userData.sessionId as string,
            userData.userId as number,
          ),
        },
      }),
    {
      enabled: !!authenticationToken,
      retry: 1,
      onSuccess: res => {
        if (res.id && !userData.userId) {
          setUserId(res.id)
        }
      },
    },
  )

  const userProfileRQData = useQuery(
    ['user-profile', router.pathname, userData],
    (): Promise<UserProfileApiResponseType> =>
      getFetchRequest({
        url: USER_PROFILE_URL,
        config: {
          headers: getUpdatedHeaders(
            headers,
            userData.sessionId as string,
            userData.userId as number,
          ),
        },
      }),
    {
      enabled: !!authenticationToken && !!userData.userId,
      retry: 1,
    },
  )

  useEffect(() => {
    // This is done to let user complete basic onboarding before accessing any logged in page
    // This logic is copied from glitter https://bitbucket.org/indwealth/glitter/src/e63d36e04597b074dbe9a465d8221ee0fcb6d4c4/src/ui/DashboardUI.js
    if (userBasicRQData.data) {
      const {
        currentStep = 1,
        level = 1,
        subLevel = 1,
        webOnboarding = { webUrl: null },
      } = userBasicRQData.data
      const shouldShowVerifyNumberScreen =
        currentStep === 0 && pathname !== '/verify-number'
      const shouldShowDashboard = currentStep > 2 || Number(level) === 3
      const shouldShowUserOnboarding = level < 3
      const shouldShowValidUserRoute =
        shouldShowDashboard && pathname && pathname !== '/'
      let computedScreenUrl = '/'

      if (shouldShowValidUserRoute) {
        computedScreenUrl = asPath
      } else if (shouldShowVerifyNumberScreen) {
        computedScreenUrl = '/verify-number'
      } else if (shouldShowDashboard) {
        computedScreenUrl = '/dashboard'
      } else if (shouldShowUserOnboarding) {
        if (webOnboarding.webUrl) {
          //Family Account accept invite screen to be opened before onboarding
          if (pathname.includes('familyAccount/')) {
            computedScreenUrl = asPath
          } else {
            computedScreenUrl = addParameterToURL(
              decodeURIComponent(webOnboarding.webUrl),
              `authorization=Bearer ${authenticationToken}&platform=web`,
            )
          }
        } else {
          computedScreenUrl = `/onboarding-l${level}-sl${subLevel}`
        }
      }

      // This is done to avoid infinite loop on same route
      if (computedScreenUrl !== asPath) {
        window.location.href = computedScreenUrl
      }
    }
  }, [asPath, authenticationToken, pathname, router, userBasicRQData.data])

  const authenticationState: TAuthenticationState = userBasicRQData.data
    ? 'LOGGED_IN'
    : 'LOGGED_OUT'

  const isAuthenticated = authenticationState === 'LOGGED_IN'
  const isNotAuthenticated = authenticationState === 'LOGGED_OUT'

  return (
    <UserRQContext.Provider
      value={{
        userBasicRQData,
        userProfileRQData,
        isAuthenticated,
        isNotAuthenticated,
        isLoadingAuthenticated: userBasicRQData.isFetching,
        authenticationState,
      }}
    >
      {children}
    </UserRQContext.Provider>
  )
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
})

export default function UserProvider({ children }: WithChildren) {
  return (
    <QueryClientProvider client={queryClient}>
      <UserFetcher>{children}</UserFetcher>
    </QueryClientProvider>
  )
}
